import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import { ThemeToggle } from "./ThemeToggle";
import { SearchOverlay } from "./SearchOverlay";
import { useTheme } from "../lib/theme";

const PRIMARY_NAV = [
  { href: "/#featured", label: "Featured" },
  { href: "/#explorer", label: "Calendar" },
  { href: "/#why", label: "Why It Matters" },
  { href: "/#preservation", label: "Preservation" },
  { href: "/#resources", label: "Resources" },
  { href: "/blog", label: "Blog" },
  { href: "/#submit", label: "Submit a Date" },
  { href: "/support", label: "Support", accent: true },
];

export const Header = ({ variant = "default" }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const { theme } = useTheme();
  const emblemSrc = theme === "dark" ? "/wnbf-emblem-light.png" : "/wnbf-emblem.png";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Cmd/Ctrl + K to toggle the search overlay anywhere in the app.
  useEffect(() => {
    const onKey = (e) => {
      const isShortcut = (e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K");
      if (isShortcut) {
        e.preventDefault();
        setSearchOpen((v) => !v);
      } else if (e.key === "/" && !searchOpen) {
        const tag = (e.target && e.target.tagName) || "";
        const editable = e.target && e.target.isContentEditable;
        if (!editable && tag !== "INPUT" && tag !== "TEXTAREA" && tag !== "SELECT") {
          e.preventDefault();
          setSearchOpen(true);
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [searchOpen]);

  return (
    <header
      style={{
        position: "sticky",
        top: 0,
        zIndex: 50,
        backdropFilter: scrolled || variant === "solid" ? "saturate(140%) blur(8px)" : "none",
        background: scrolled || variant === "solid" ? "hsl(var(--bg) / 0.85)" : "hsl(var(--bg))",
        borderBottom: scrolled || variant === "solid" ? "1px solid hsl(var(--line))" : "1px solid transparent",
        transition: "background-color 240ms ease, border-color 240ms ease",
      }}
      role="banner"
    >
      <a className="skip-link" href="#main">Skip to main content</a>
      <div className="shell" style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        height: 92,
      }}>
        <Link to="/" aria-label="Will Not Be Forgotten — home" style={{ textDecoration: "none", color: "hsl(var(--ink))", display: "inline-flex", alignItems: "center" }}>
          <img
            src={emblemSrc}
            alt="Will Not Be Forgotten"
            className="wnbf-emblem"
            style={{ height: 64, width: "auto", display: "block" }}
          />
        </Link>
        <nav aria-label="Primary" className="hidden md:flex" style={{ gap: 22, alignItems: "center" }}>
          {PRIMARY_NAV.map((item) => (
            <a
              key={item.href}
              href={item.href}
              data-testid={item.accent ? "nav-support" : undefined}
              style={item.accent ? {
                fontFamily: "var(--font-body)",
                fontSize: "0.82rem",
                fontWeight: 600,
                letterSpacing: "0.01em",
                textDecoration: "none",
                color: "hsl(var(--accent-ink))",
                background: "hsl(var(--accent))",
                padding: "0.45rem 0.95rem",
                borderRadius: 999,
                border: "1px solid hsl(var(--accent))",
                transition: "background-color 160ms ease",
              } : {
                fontFamily: "var(--font-body)",
                fontSize: "0.85rem",
                color: "hsl(var(--ink-soft))",
                textDecoration: "none",
                fontWeight: 500,
                letterSpacing: "0.01em",
              }}
            >
              {item.label}
            </a>
          ))}
        </nav>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            aria-label="Search the archive"
            data-testid="header-search-toggle"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              height: 38,
              padding: "0 14px",
              borderRadius: 999,
              border: "1px solid hsl(var(--line))",
              background: "hsl(var(--bg-elev))",
              color: "hsl(var(--ink-soft))",
              cursor: "pointer",
              fontFamily: "var(--font-body)",
              fontSize: "0.82rem",
              transition: "border-color 160ms ease, color 160ms ease",
            }}
          >
            <Search size={14} aria-hidden />
            <span className="hidden md:inline">Search</span>
            <span
              aria-hidden
              className="hidden md:inline"
              style={{
                display: "inline-block",
                padding: "1px 6px",
                marginLeft: 4,
                border: "1px solid hsl(var(--line-strong))",
                borderRadius: 3,
                fontSize: "0.68rem",
                color: "hsl(var(--muted))",
                background: "hsl(var(--bg))",
                letterSpacing: 0,
              }}
            >
              ⌘K
            </span>
          </button>
          <ThemeToggle />
          <button
            type="button"
            aria-expanded={open}
            aria-controls="mobile-nav"
            onClick={() => setOpen((v) => !v)}
            className="flex md:hidden"
            data-testid="mobile-nav-toggle"
            style={{
              alignItems: "center",
              justifyContent: "center",
              width: 38, height: 38,
              borderRadius: 999,
              border: "1px solid hsl(var(--line))",
              background: "hsl(var(--bg-elev))",
              color: "hsl(var(--ink))",
              cursor: "pointer",
            }}
          >
            <span aria-hidden style={{ fontSize: 16 }}>{open ? "×" : "≡"}</span>
            <span className="sr-only">Toggle navigation</span>
          </button>
        </div>
      </div>
      {open && (
        <nav id="mobile-nav" aria-label="Mobile" className="md:hidden" style={{
          borderTop: "1px solid hsl(var(--line))",
          background: "hsl(var(--bg))",
        }}>
          <ul style={{ listStyle: "none", margin: 0, padding: "0.75rem 1.5rem 1.25rem" }}>
            {PRIMARY_NAV.map((item) => (
              <li key={item.href}>
                <a
                  href={item.href}
                  onClick={() => setOpen(false)}
                  style={{
                    display: "block",
                    padding: "0.65rem 0",
                    fontFamily: "var(--font-body)",
                    color: "hsl(var(--ink))",
                    textDecoration: "none",
                    borderBottom: "1px solid hsl(var(--line) / 0.6)",
                  }}
                >
                  {item.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      )}
      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
    </header>
  );
};
